import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #isAutoFixing = false;

  /** @type {boolean} */
  #didAutoFixRefetch = false;

  connectedCallback() {
    super.connectedCallback();
    this.rebuildRadiosCache();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Rebuild radios + checked state caches (needed after morph).
   */
  rebuildRadiosCache() {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
  }

  /**
   * Auto-select first available option in ANY selector (fieldset/select) if current selection is missing/invalid/hidden/unavailable.
   * Returns true if it changed at least one selector.
   * @returns {boolean}
   */
  autoFixAllSelectors() {
    if (this.#isAutoFixing) return false;
    this.#isAutoFixing = true;

    let changed = false;

    try {
      // --- RADIO FIELDSETS ---
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

      fieldsets.forEach((fieldset) => {
        const radios = /** @type {HTMLInputElement[]} */ (
          Array.from(fieldset.querySelectorAll('input[type="radio"]'))
        );
        if (!radios.length) return;

        const checked = radios.find((r) => r.checked) || radios.find((r) => r.dataset.currentChecked === 'true');
        const checkedLabel = checked?.closest('label');

        // If label is not in DOM (removed) or hidden, treat as invalid
        const checkedVisible = checkedLabel ? getComputedStyle(checkedLabel).display !== 'none' : false;
        const checkedAvailable = checked ? checked.dataset.optionAvailable !== 'false' : false;

        if (!checked || !checkedAvailable || !checkedVisible) {
          const firstAvailable = radios.find((r) => {
            const label = r.closest('label');
            if (!label) return false;
            if (getComputedStyle(label).display === 'none') return false;
            return r.dataset.optionAvailable !== 'false';
          });

          if (firstAvailable) {
            this.updateSelectedOption(firstAvailable);
            changed = true;
          }
        }
      });

      // --- DROPDOWNS ---
      const selects = /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('select')));

      selects.forEach((select) => {
        const selected = select.options[select.selectedIndex];

        const selectedOk =
          selected && !selected.disabled && !selected.hidden && selected.dataset.optionAvailable !== 'false';

        if (!selectedOk) {
          const firstAvailable = Array.from(select.options).find(
            (opt) => !opt.disabled && !opt.hidden && opt.dataset.optionAvailable !== 'false'
          );

          if (firstAvailable) {
            select.value = firstAvailable.value;
            this.updateSelectedOption(select);
            changed = true;
          }
        }
      });
    } finally {
      this.#isAutoFixing = false;
    }

    return changed;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.#isAutoFixing) return;

    const selectedOptionFromTarget =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOptionFromTarget) return;

    // Apply selection state
    this.updateSelectedOption(event.target);

    // Dispatch "selected" based on CURRENT selectedOption
    const selectedOption = this.selectedOption;
    if (!selectedOption) return;

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // reset auto-fix refetch guard for this user action
    this.#didAutoFixRefetch = false;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }

    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }

      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl
   * @param {boolean} shouldMorphMain
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
          return;
        }

        const newProduct = this.updateVariantPicker(html);

        // ✅ After morph: rebuild caches, then auto-fix invalid selectors
        this.rebuildRadiosCache();

        const didChange = this.autoFixAllSelectors();

        // ✅ If auto-fix changed something, do ONE extra refetch with the corrected selection
        if (didChange && !this.#didAutoFixRefetch) {
          this.#didAutoFixRefetch = true;

          const finalSelected = this.selectedOption;
          if (finalSelected) {
            this.dispatchEvent(new VariantSelectedEvent({ id: finalSelected.dataset.optionValueId ?? '' }));
            this.fetchUpdatedSection(this.buildRequestUrl(finalSelected), false);
            return;
          }
        }

        // Normal update event
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  /**
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
    return selectedOption;
  }

  /**
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;

    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');

    return optionValueId;
  }

  /**
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
